import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FleetBoardCard, FleetBoardLane, GetFleetBoardResponse } from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { applyMove, applyQueueOverrides, neighbourOf, rankProjectOf, type QueueByLane } from '@/lib/fleet/board-queue';

const NOT_IN_QUEUE = 'fleet.queue.not-in-queue';
const GENERIC_ERROR = 'generic.unexpected';

type Pending = { readonly queue: ReadonlyArray<FleetBoardCard>; readonly requesting: boolean };

function errorCodeOf(body: unknown): string | undefined {
  return (body as { error?: { code?: string } } | undefined)?.error?.code;
}

export type QueueRanking = {
  /** The polled board with each lane's queue as the operator has it now. */
  readonly board: GetFleetBoardResponse;
  /** The lanes whose rank request is in flight: no second drag until it settles. */
  readonly busy: ReadonlySet<string>;
  /** An error code for `errors.*`, set by a failed move and cleared by the next drag. */
  readonly errorCode: string | null;
  readonly onDragStart: (lane: FleetBoardLane) => void;
  readonly onDragCancel: (lane: FleetBoardLane) => void;
  readonly onMove: (lane: FleetBoardLane, from: number, to: number) => void;
};

/**
 * Ordering a lane's queue by hand, and what the page shows meanwhile. Two overrides sit over the polled board, and a
 * poll answer never reaches a lane they cover:
 *
 * - `pending` - the lane's queue frozen from the start of a drag, then the optimistic order while the `rank` request is
 *   in flight. A failure drops it (the card is back where the poll or the last answer had it); a success replaces it.
 * - `served` - the queue the `rank` answer returned, shown until the next poll answers. A re-read follows the move at
 *   once, which also discards a read that was already in flight and may predate it.
 *
 * `reread` makes the page read the board now.
 */
export function useQueueRanking(polled: GetFleetBoardResponse, reread: () => void): QueueRanking {
  const [pending, setPending] = useState<Readonly<Record<string, Pending>>>({});
  const [served, setServed] = useState<QueueByLane>({});
  const [errorCode, setErrorCode] = useState<string | null>(null);

  // A poll that answers is newer than any `rank` answer: the server's own order replaces it.
  useEffect(() => {
    setServed({});
  }, [polled]);

  const drop = useCallback((laneName: string) => {
    setPending((previous) => {
      const { [laneName]: _removed, ...rest } = previous;

      return rest;
    });
  }, []);

  const onDragStart = useCallback((lane: FleetBoardLane) => {
    setErrorCode(null);
    setPending((previous) => ({ ...previous, [lane.name]: { queue: lane.queue, requesting: false } }));
  }, []);

  const onDragCancel = useCallback((lane: FleetBoardLane) => drop(lane.name), [drop]);

  const onMove = useCallback(
    (lane: FleetBoardLane, from: number, to: number) => {
      const queue = lane.queue;
      const moved = applyMove(queue, from, to);
      const card = queue[from];
      const project = rankProjectOf(lane);
      const neighbour = neighbourOf(moved, to);

      if (moved === queue || card === undefined || project === null || neighbour === null) {
        drop(lane.name);

        return;
      }

      setPending((previous) => ({ ...previous, [lane.name]: { queue: moved, requesting: true } }));

      void (async () => {
        let code: string | null = null;

        try {
          const result = await apiClient.adminFleetBoard.rank({
            body: { project, lane: lane.name, ticket: card.identifier, ...neighbour },
          });

          if (result.ok) {
            setServed((previous) => ({ ...previous, [lane.name]: result.body.queue }));
          } else {
            code = errorCodeOf(result.body) === NOT_IN_QUEUE ? NOT_IN_QUEUE : GENERIC_ERROR;
          }
        } catch {
          code = GENERIC_ERROR;
        }

        drop(lane.name);

        if (code !== null) setErrorCode(code);

        // The move changed what the board answers; and a stale board is what a "not in queue" means.
        if (code === null || code === NOT_IN_QUEUE) reread();
      })();
    },
    [drop, reread],
  );

  const busy = useMemo(
    () => new Set(Object.entries(pending).flatMap(([name, entry]) => (entry.requesting ? [name] : []))),
    [pending],
  );

  const board = useMemo(
    () =>
      applyQueueOverrides(polled, {
        ...served,
        ...Object.fromEntries(Object.entries(pending).map(([name, entry]) => [name, entry.queue])),
      }),
    [polled, served, pending],
  );

  return { board, busy, errorCode, onDragStart, onDragCancel, onMove };
}
